import React from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { BRAND_GREEN, BRAND_GOLD } from '../../theme/sucronTheme';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/**
 * BarComparisonChart - Comparação de métricas entre ligas
 * 
 * @param {Array} leagues - Array de objetos com dados das ligas
 * @param {String} metric - Nome da métrica a ser exibida
 * @param {String} metricLabel - Label amigável da métrica
 * 
 * Exemplo:
 * leagues = [
 *   { league_name: 'Premier League', goals_per_game: 2.8 },
 *   { league_name: 'La Liga', goals_per_game: 2.5 },
 *   { league_name: 'Serie A', goals_per_game: 2.3 }
 * ]
 * metric = 'goals_per_game'
 * metricLabel = 'Gols por Jogo'
 */
export default function BarComparisonChart({ leagues = [], metric, metricLabel }) {
  if (!leagues || leagues.length === 0 || !metric) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <p>Nenhum dado disponível para comparação</p>
      </div>
    );
  }

  const labels = leagues.map(l => l.league_name || 'Liga Desconhecida');
  
  // Gerar gradiente de cores do verde ao dourado
  const generateColors = (count) => {
    const colors = [];
    for (let i = 0; i < count; i++) {
      const ratio = i / Math.max(count - 1, 1);
      // Interpolar entre verde e dourado
      const r = Math.round(6 + (180 - 6) * ratio);
      const g = Math.round(78 + (140 - 78) * ratio);
      const b = Math.round(59 + (44 - 59) * ratio);
      colors.push(`rgb(${r}, ${g}, ${b})`);
    }
    return colors;
  };

  const backgroundColors = generateColors(leagues.length);

  const data = {
    labels,
    datasets: [{
      label: metricLabel || 'Métrica',
      data: leagues.map(l => l[metric] || 0),
      backgroundColor: backgroundColors,
      borderColor: BRAND_GOLD,
      borderWidth: 2,
      borderRadius: 8,
      borderSkipped: false,
    }]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: metricLabel || 'Comparação de Métricas',
        color: '#1f2937',
        font: {
          size: 16,
          weight: '700'
        },
        padding: {
          top: 0,
          bottom: 20
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: BRAND_GOLD,
        borderWidth: 1,
        padding: 12,
        displayColors: false,
        callbacks: {
          label: function(context) {
            const value = context.parsed.y;
            return `${metricLabel}: ${value.toFixed(2)}`;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          color: '#6b7280',
          font: {
            size: 11,
            weight: '500'
          },
          callback: function(value) {
            return value.toFixed(1);
          }
        },
        grid: {
          color: 'rgba(107, 114, 128, 0.1)',
          lineWidth: 1
        },
        border: {
          display: false
        }
      },
      x: {
        ticks: {
          color: '#374151',
          font: {
            size: 12,
            weight: '600'
          },
          maxRotation: 45,
          minRotation: 0
        },
        grid: {
          display: false
        },
        border: {
          display: false
        }
      }
    },
    interaction: {
      mode: 'index',
      intersect: false
    }
  };

  return (
    <div style={{ height: '350px', position: 'relative', width: '100%' }}>
      <Bar data={data} options={options} />
    </div>
  );
}
